import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_SITE } from './siteDefaults';
import { resolveHeader } from './siteHeader';
import { resolveSiteSeo } from './siteSeo';
import { resolveFarmer } from './siteFarmer';
import { apiUrl } from './apiBase';
import DocumentHead from './DocumentHead';
import SiteFooter from './components/SiteFooter';
import MarketingSiteHeader from './components/MarketingSiteHeader';
import marketingHeaderNavIsCurrent from './marketingHeaderNavIsCurrent';

function FarmerStorySection({ story }) {
  return (
    <section className="relative border-t border-neutral-200/80 bg-gradient-to-b bg-white" aria-labelledby="farmer-story-heading">
      <div className="mx-auto max-w-shell px-4 py-16 sm:px-6 sm:py-20 md:px-8 md:py-24">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16 xl:gap-20">
          <div className="relative order-1 lg:order-1">
            <div
              className="pointer-events-none absolute -left-6 -top-6 h-28 w-28 rounded-full bg-brand-lime/25 blur-2xl sm:-left-10 sm:-top-10 sm:h-36 sm:w-36"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -bottom-8 -right-4 h-32 w-32 rounded-full bg-brand-forest/10 blur-2xl sm:-bottom-12 sm:right-0"
              aria-hidden
            />

            <div className="relative mx-auto max-w-lg lg:mx-0">
              <div className="rounded-[1.75rem] bg-gradient-to-br from-brand-lime/70 via-brand-lime-soft/40 to-brand-forest/25 p-[3px] shadow-[0_24px_60px_-20px_rgba(20,38,28,0.35)]">
                <div className="relative overflow-hidden rounded-[1.6rem] bg-neutral-100 ring-1 ring-black/[0.06]">
                  <div
                    className="absolute inset-0 bg-gradient-to-tr from-brand-forest/25 via-transparent to-transparent"
                    aria-hidden
                  />
                  <div
                    className="absolute left-4 top-4 z-[1] rounded-full bg-white/90 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-widest text-brand-forest shadow-sm ring-1 ring-black/[0.04] backdrop-blur-sm sm:left-5 sm:top-5 sm:px-3.5 sm:py-1.5 sm:text-xs"
                  >
                    {story.badge}
                  </div>
                  <img
                    src={story.image}
                    alt={story.imageAlt}
                    className="aspect-[4/5] w-full scale-[1.32] object-cover object-[50%_2%] [transform-origin:50%_4%] sm:aspect-[5/6] sm:scale-[1.28] sm:object-[50%_0%] sm:[transform-origin:50%_3%] lg:min-h-[min(100vw,28rem)] lg:scale-[1.38] lg:object-[50%_0%] lg:[transform-origin:50%_2%]"
                    loading="lazy"
                    decoding="async"
                  />
                  <div
                    className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-brand-forest/50 to-transparent"
                    aria-hidden
                  />
                  <div
                    className="absolute bottom-4 left-4 right-4 z-[1] rounded-xl border border-white/25 bg-white/15 px-3 py-2 text-xs font-medium text-white shadow-lg backdrop-blur-md sm:bottom-5 sm:left-5 sm:right-5 sm:px-4 sm:text-sm"
                  >
                    {story.caption}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="order-2 lg:order-2">
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.2em] text-brand-lime">{story.eyebrow}</p>
            <h2
              id="farmer-story-heading"
              className="mt-3 text-3xl font-bold tracking-tight text-brand-forest sm:text-4xl"
            >
              {story.title}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-700 sm:text-lg">{story.body}</p>
            {story.bullets.length > 0 ? (
              <ul className="mt-8 max-w-xl space-y-4 p-0">
                {story.bullets.map((line) => (
                  <li
                    key={line}
                    className="flex gap-3 text-left text-[0.95rem] leading-relaxed text-neutral-800 sm:text-base"
                  >
                    <span
                      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-lime/25 text-brand-forest ring-1 ring-brand-lime/40"
                      aria-hidden
                    >
                      <svg viewBox="0 0 12 12" className="h-3.5 w-3.5" fill="none">
                        <path
                          d="M2 6l2.5 2.5L10 3"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function FarmerClosingSection({ closing }) {
  return (
    <section className="relative bg-white pb-20 pt-4 sm:pb-24" aria-labelledby="farmer-closing-heading">
      <div className="mx-auto max-w-shell px-4 sm:px-6 md:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-brand-forest px-6 py-10 text-white shadow-[0_20px_50px_-12px_rgba(20,38,28,0.35)] ring-1 ring-white/10 sm:px-10 sm:py-12 lg:px-14 lg:py-14">
          <div
            className="pointer-events-none absolute -right-24 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-brand-lime/20 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -left-20 bottom-0 h-56 w-56 rounded-full bg-white/5 blur-3xl"
            aria-hidden
          />
          <header className="relative max-w-3xl">
            <p className="mb-3 inline-block rounded-md bg-white/15 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-brand-lime-soft sm:text-xs">
              {closing.eyebrow}
            </p>
            <h2
              id="farmer-closing-heading"
              className="m-0 text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl lg:text-[1.85rem] lg:leading-snug"
            >
              {closing.title}
            </h2>
            <p className="relative z-[1] m-0 mt-5 max-w-2xl text-sm leading-relaxed text-white/85 sm:mt-6 sm:text-[0.95rem]">
              {closing.body}
            </p>
          </header>
        </div>
      </div>
    </section>
  );
}

export default function FarmerPage() {
  const [site, setSite] = useState(() => JSON.parse(JSON.stringify(DEFAULT_SITE)));

  const headerSection = useMemo(() => resolveHeader(site), [site]);
  const farmer = useMemo(() => resolveFarmer(site), [site]);
  const baseSeo = useMemo(() => resolveSiteSeo(site), [site]);

  const pageSeo = useMemo(() => {
    const brand = `${headerSection.brandPrimary} ${headerSection.brandSecondary}`.trim() || 'Rafid Seed';
    return {
      ...baseSeo,
      htmlTitle: `Farmer story | ${brand}`,
      socialTitle: `Farmer story — ${baseSeo.socialTitle || brand}`,
      canonicalUrl: typeof window !== 'undefined' ? `${window.location.origin}/farmer` : '',
    };
  }, [baseSeo, headerSection.brandPrimary, headerSection.brandSecondary]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    fetch(apiUrl('/api/site'))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('unreachable'))))
      .then(setSite)
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <DocumentHead seo={pageSeo} />

      <MarketingSiteHeader
        site={site}
        idPrefix="farmer"
        isNavLinkCurrent={marketingHeaderNavIsCurrent}
      />

      <section className="relative isolate min-h-[min(26vh,240px)] overflow-hidden pt-[calc(3.85rem+env(safe-area-inset-top))] sm:min-h-[min(30vh,280px)] sm:pt-[calc(4.25rem+env(safe-area-inset-top))] md:pt-[calc(4.5rem+env(safe-area-inset-top))] pb-0">
        <div
          className={`absolute inset-0 bg-cover bg-center ${farmer.pageHeroImage ? '' : 'bg-neutral-300'}`}
          style={farmer.pageHeroImage ? { backgroundImage: `url(${farmer.pageHeroImage})` } : undefined}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b " aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-brand-forest/85 to-transparent" aria-hidden />
        <div className="relative z-[1] mx-auto max-w-shell px-4 mt-40">
          <div className="relative">
            <div className="relative overflow-hidden rounded-t-[60px] bg-[#e8f5e9] px-6 pb-10 pt-10 text-center shadow-[0_-20px_60px_rgba(0,0,0,0.15)]">
              <div className="absolute -left-16 top-6 h-32 w-32 rounded-full bg-[#f3f4f6]" aria-hidden />
              <div className="absolute -right-16 top-6 h-32 w-32 rounded-full bg-[#f3f4f6]" aria-hidden />
              <h1 className="text-3xl font-bold text-brand-forest">{farmer.heroTitle}</h1>
              <p className="mt-3 flex flex-wrap justify-center gap-2 text-xs uppercase text-brand-lime">
                <Link to="/" className="no-underline text-brand-lime hover:text-brand-forest">
                  {farmer.heroBreadcrumbHome}
                </Link>
                <span aria-hidden>:</span>
                <span className="text-brand-forest">{farmer.heroBreadcrumbCurrent}</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <main className="relative bg-white">
        <FarmerStorySection story={farmer.story} />
        <FarmerClosingSection closing={farmer.closing} />
      </main>

      <SiteFooter site={site} headerSection={headerSection} />
    </div>
  );
}
